import { useEffect, useState, useRef } from 'react'
import AnimatedLetters from '../AnimatedLetters'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import profile from '../../data/profiles/activeProfile'
import './index.scss'

const { contact, about } = profile

const CHANNELS = [
  {
    label: 'Email',
    value: contact.email,
    href: `mailto:${contact.email}`,
    cursor: 'write',
  },
  {
    label: 'Phone',
    value: contact.phone,
    href: `tel:${contact.phone.replace(/[\s\-()]/g, '')}`,
    cursor: 'call',
  },
  {
    label: 'LinkedIn',
    value: contact.linkedin.label,
    href: contact.linkedin.url,
    external: true,
    cursor: 'connect',
  },
  {
    label: 'GitHub',
    value: contact.github.label,
    href: contact.github.url,
    external: true,
    cursor: 'follow',
  },
  {
    label: 'Location',
    value: contact.location,
    href: null,
  },
]

const FIELDS = [
  { id: 'name', name: 'name', label: 'Name', type: 'text' },
  { id: 'email', name: 'email', label: 'Email', type: 'email' },
  { id: 'subject', name: 'subject', label: 'Subject', type: 'text' },
]

const Contact = () => {
  const [letterClass, setLetterClass] = useState('text-animate')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState(null) // 'success' | 'error'
  const form = useRef()

  useEffect(() => {
    const timer = setTimeout(() => setLetterClass('text-animate-hover'), 3000)
    return () => clearTimeout(timer)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus(null)

    const data = Object.fromEntries(new FormData(e.target))

    try {
      // 1 — Netlify Forms (production only — endpoint doesn't exist in local dev)
      if (process.env.NODE_ENV === 'production') {
        await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ 'form-name': 'contact', ...data }).toString(),
        })
      }

      // 2 — WhatsApp via Netlify function (silent fail in local dev)
      await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).catch(() => {})

      // Reset BEFORE setStatus — React 17 flushes state synchronously,
      // which would unmount the form and make form.current null
      form.current?.reset()
      setStatus('success')
    } catch (err) {
      console.error(err)
      setStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="ct-page">
      {/* ── Ghost word backdrop ── */}
      <span className="ct-ghost" aria-hidden="true">
        Hello
      </span>

      {/* ── Left: the pitch + channels ── */}
      <div className="ct-left">
        <p className="ct-kicker">
          <span className="ct-kicker__rule" />
          Correspondence
        </p>

        <h1 className="ct-title">
          <span className="ct-title__line">
            <AnimatedLetters
              letterClass={letterClass}
              strArray={"Let's work".split('')}
              idx={10}
            />
          </span>
          <em className="ct-title__line ct-title__line--em">
            <AnimatedLetters
              letterClass={letterClass}
              strArray={'together.'.split('')}
              idx={21}
            />
          </em>
        </h1>

        <p className="ct-sub">
          Have a role in mind or want to collaborate? The fastest route is a
          straight line:
        </p>

        <a
          className="ct-mailto"
          href={`mailto:${contact.email}`}
          data-cursor="write"
        >
          {contact.email}
          <span className="ct-mailto__arrow" aria-hidden="true">
            ↗
          </span>
        </a>

        <section className="ct-channels">
          <h2 className="ct-rule-title">
            <span className="ct-rule-title__index">01</span>
            <span className="ct-rule-title__text">Channels</span>
            <span className="ct-rule-title__line" />
          </h2>

          {CHANNELS.map((ch, i) => {
            const inner = (
              <>
                <span className="ct-row__num">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="ct-row__label">{ch.label}</span>
                <span className="ct-row__leader" />
                <span className="ct-row__value">
                  {ch.value}
                  {ch.external && (
                    <span className="ct-row__ext" aria-hidden="true">
                      ↗
                    </span>
                  )}
                </span>
              </>
            )
            return ch.href ? (
              <a
                key={ch.label}
                href={ch.href}
                className="ct-row"
                style={{ '--i': i }}
                target={ch.external ? '_blank' : undefined}
                rel={ch.external ? 'noreferrer' : undefined}
                data-cursor={ch.cursor}
              >
                {inner}
              </a>
            ) : (
              <div
                key={ch.label}
                className="ct-row ct-row--static"
                style={{ '--i': i }}
              >
                {inner}
              </div>
            )
          })}
        </section>

        <p className="ct-status">
          <span className="ct-status__dot" />
          <span className="ct-status__text">{about.availability}</span>
          <span className="ct-status__sep">/</span>
          <span className="ct-status__note">replies within 24 hours</span>
        </p>
      </div>

      {/* ── Right: the letter ── */}
      <div className="ct-right">
        <div className="ct-letter">
          <div className="ct-letter__head">
            <h2 className="ct-rule-title">
              <span className="ct-rule-title__index">02</span>
              <span className="ct-rule-title__text">Write a message</span>
              <span className="ct-rule-title__line" />
            </h2>
            <p className="ct-letter__title">
              Start a <em>conversation.</em>
            </p>
          </div>

          {status === 'success' ? (
            <div className="ct-sent">
              <div className="ct-sent__icon">
                <FontAwesomeIcon icon={faPaperPlane} />
              </div>
              <h3 className="ct-sent__title">
                Message <em>sent.</em>
              </h3>
              <p className="ct-sent__note">
                Thanks for reaching out — expect a reply within 24 hours.
              </p>
              <button
                className="ct-sent__again"
                onClick={() => setStatus(null)}
              >
                Write another
              </button>
            </div>
          ) : (
            <form
              ref={form}
              onSubmit={handleSubmit}
              name="contact"
              data-netlify="true"
              netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" name="bot-field" />

              {FIELDS.map((f, i) => (
                <div key={f.id} className="ct-field">
                  <span className="ct-field__num">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="ct-field__control">
                    <input
                      type={f.type}
                      name={f.name}
                      id={f.id}
                      placeholder=" "
                      required
                    />
                    <label htmlFor={f.id}>{f.label}</label>
                    <span className="ct-field__ink" />
                  </div>
                </div>
              ))}

              <div className="ct-field ct-field--area">
                <span className="ct-field__num">04</span>
                <div className="ct-field__control">
                  <textarea
                    name="message"
                    id="message"
                    placeholder=" "
                    rows={5}
                    required
                  />
                  <label htmlFor="message">Message</label>
                  <span className="ct-field__ink" />
                </div>
              </div>

              {status === 'error' && (
                <p className="ct-error">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                className="ct-send"
                disabled={isSubmitting}
                data-cursor="send"
              >
                <span>{isSubmitting ? 'Sending…' : 'Send message'}</span>
                <span className="ct-send__arrow" aria-hidden="true">
                  {isSubmitting ? '…' : '↗'}
                </span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default Contact
